import { type OutreachType } from '@apps/web/lib/outreach_step_types';

export const MINUTES_PER_HOUR = 60;
export const MAX_OUTREACH_STEP_DELAY_MINUTES = 59;
export const MAX_OUTREACH_STEP_DELAY_HOURS = 24;
export const MAX_OUTREACH_STEP_DELAY_TOTAL_MINUTES =
    MAX_OUTREACH_STEP_DELAY_HOURS * MINUTES_PER_HOUR;
export const CLOCK_REMINDER_OFFSET_MAX_MINUTES = 120;
export const OUTREACH_STEP_DELAY_INPUT_PATTERN = '-?\\d+';

const INTEGER_INPUT_PATTERN = /^-?\d+$/;

type OutreachStepDelayParts = {
    readonly hours: number;
    readonly minutes: number;
};

export type OutreachStepDelayPartsInput = {
    readonly hours: string;
    readonly minutes: string;
};

export type OutreachStepDelayInvalidParts = {
    readonly hours: boolean;
    readonly minutes: boolean;
};

export type OutreachStepDelayInputEvaluation =
    | {
          readonly status: 'invalid';
          readonly invalidParts: OutreachStepDelayInvalidParts;
      }
    | {
          readonly status: 'incomplete';
          readonly invalidParts: OutreachStepDelayInvalidParts;
      }
    | {
          readonly status: 'valid';
          readonly delayInMinutes: number;
          readonly displayValue: OutreachStepDelayPartsInput;
          readonly invalidParts: OutreachStepDelayInvalidParts;
          readonly shouldReplaceInput: boolean;
      };

type ParsedIntegerDraft =
    | { readonly status: 'empty' }
    | { readonly status: 'invalid' }
    | { readonly status: 'valid'; readonly value: number };

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function parseIntegerDraft(rawValue: string): ParsedIntegerDraft {
    const trimmed = rawValue.trim();
    if (trimmed === '') {
        return { status: 'empty' };
    }
    if (!INTEGER_INPUT_PATTERN.test(trimmed)) {
        return { status: 'invalid' };
    }
    return { status: 'valid', value: Number.parseInt(trimmed, 10) };
}

function coerceValidIntegerDraft(
    parsed: Extract<ParsedIntegerDraft, { readonly status: 'valid' }>,
    { min, max }: { readonly min: number; readonly max: number },
): number {
    return clamp(parsed.value, min, max);
}

export function getMinimumDelayForOutreachStep(
    index: number,
    outreachType: OutreachType,
): number {
    // The first step of a clock reminder is an offset from the shift
    // boundary and may be negative so the reminder fires before the shift
    // start/end. All other step delays are relative waits after the
    // previous step, so they must be non-negative.
    if (
        index === 0 &&
        (outreachType === 'clock_in' || outreachType === 'clock_out')
    ) {
        return -CLOCK_REMINDER_OFFSET_MAX_MINUTES;
    }
    return index === 0 ? 0 : 1;
}

/**
 * Whether a step's delay field offers a minutes/hours unit toggle. The first
 * step of a clock reminder is a signed offset from the shift boundary with its
 * own bounds, so it stays minutes-only.
 */
export function outreachStepDelaySupportsUnitToggle({
    index,
    outreachType,
}: {
    readonly index: number;
    readonly outreachType: OutreachType;
}): boolean {
    return !(
        index === 0 &&
        (outreachType === 'clock_in' || outreachType === 'clock_out')
    );
}

export function getMaximumDelayForOutreachStep(
    index: number,
    outreachType: OutreachType,
): number {
    return outreachStepDelaySupportsUnitToggle({ index, outreachType })
        ? MAX_OUTREACH_STEP_DELAY_TOTAL_MINUTES
        : CLOCK_REMINDER_OFFSET_MAX_MINUTES;
}

export function normalizeOutreachStepDelayInMinutes({
    index,
    outreachType,
    delayInMinutes,
}: {
    readonly index: number;
    readonly outreachType: OutreachType;
    readonly delayInMinutes: number;
}): number {
    return clamp(
        delayInMinutes,
        getMinimumDelayForOutreachStep(index, outreachType),
        getMaximumDelayForOutreachStep(index, outreachType),
    );
}

/** Convert a stored minutes value into exact hours/minutes display parts. */
function outreachStepDelayMinutesToParts(
    delayInMinutes: number,
): OutreachStepDelayParts {
    const clampedMinutes = clamp(
        delayInMinutes,
        0,
        MAX_OUTREACH_STEP_DELAY_TOTAL_MINUTES,
    );
    return {
        hours: Math.floor(clampedMinutes / MINUTES_PER_HOUR),
        minutes: clampedMinutes % MINUTES_PER_HOUR,
    };
}

export function getOutreachStepDelayInputValue({
    index,
    outreachType,
    delayInMinutes,
}: {
    readonly index: number;
    readonly outreachType: OutreachType;
    readonly delayInMinutes: number;
}): OutreachStepDelayPartsInput {
    const normalizedDelayInMinutes = normalizeOutreachStepDelayInMinutes({
        index,
        outreachType,
        delayInMinutes,
    });
    if (!outreachStepDelaySupportsUnitToggle({ index, outreachType })) {
        return {
            hours: '0',
            minutes: normalizedDelayInMinutes.toString(),
        };
    }

    const parts = outreachStepDelayMinutesToParts(normalizedDelayInMinutes);
    return {
        hours: parts.hours.toString(),
        minutes: parts.minutes.toString(),
    };
}

function shouldReplaceMinutesOnlyDraft({
    parsedMinutes,
    min,
    max,
}: {
    readonly parsedMinutes: ParsedIntegerDraft;
    readonly min: number;
    readonly max: number;
}): boolean {
    return (
        parsedMinutes.status === 'valid' &&
        (parsedMinutes.value < min || parsedMinutes.value > max)
    );
}

function shouldReplaceHoursMinutesDraft({
    parsedHours,
    parsedMinutes,
}: {
    readonly parsedHours: ParsedIntegerDraft;
    readonly parsedMinutes: ParsedIntegerDraft;
}): boolean {
    if (
        parsedHours.status === 'valid' &&
        (parsedHours.value < 0 ||
            parsedHours.value > MAX_OUTREACH_STEP_DELAY_HOURS)
    ) {
        return true;
    }
    if (
        parsedMinutes.status === 'valid' &&
        (parsedMinutes.value < 0 ||
            parsedMinutes.value > MAX_OUTREACH_STEP_DELAY_MINUTES)
    ) {
        return true;
    }
    if (parsedHours.status !== 'valid' || parsedMinutes.status !== 'valid') {
        return false;
    }
    return (
        parsedHours.value * MINUTES_PER_HOUR + parsedMinutes.value >
        MAX_OUTREACH_STEP_DELAY_TOTAL_MINUTES
    );
}

/**
 * Evaluate the editable draft for a step delay. Numeric drafts are always
 * coercible into the valid storage range, so typing 60 minutes immediately
 * normalizes back to 59. Empty drafts are incomplete so clearing a field to
 * type does not commit a new delay; non-numeric drafts are invalid until blur
 * restores the last committed delay.
 */
export function evaluateOutreachStepDelayInput({
    index,
    outreachType,
    input,
}: {
    readonly index: number;
    readonly outreachType: OutreachType;
    readonly input: OutreachStepDelayPartsInput;
}): OutreachStepDelayInputEvaluation {
    const supportsHoursMinutes = outreachStepDelaySupportsUnitToggle({
        index,
        outreachType,
    });
    const parsedHours = parseIntegerDraft(input.hours);
    const parsedMinutes = parseIntegerDraft(input.minutes);
    const invalidParts = {
        hours: supportsHoursMinutes && parsedHours.status === 'invalid',
        minutes: parsedMinutes.status === 'invalid',
    };
    if (invalidParts.hours || invalidParts.minutes) {
        return { status: 'invalid', invalidParts };
    }

    if (!supportsHoursMinutes) {
        if (parsedMinutes.status !== 'valid') {
            return { status: 'incomplete', invalidParts };
        }
        const min = getMinimumDelayForOutreachStep(index, outreachType);
        const max = getMaximumDelayForOutreachStep(index, outreachType);
        const delayInMinutes = coerceValidIntegerDraft(parsedMinutes, {
            min,
            max,
        });
        return {
            status: 'valid',
            delayInMinutes,
            displayValue: {
                hours: '0',
                minutes: delayInMinutes.toString(),
            },
            invalidParts,
            shouldReplaceInput: shouldReplaceMinutesOnlyDraft({
                parsedMinutes,
                min,
                max,
            }),
        };
    }

    if (parsedHours.status !== 'valid' || parsedMinutes.status !== 'valid') {
        return { status: 'incomplete', invalidParts };
    }

    const hours = coerceValidIntegerDraft(parsedHours, {
        min: 0,
        max: MAX_OUTREACH_STEP_DELAY_HOURS,
    });
    const minutes = coerceValidIntegerDraft(parsedMinutes, {
        min: 0,
        max: MAX_OUTREACH_STEP_DELAY_MINUTES,
    });
    const delayInMinutes = normalizeOutreachStepDelayInMinutes({
        index,
        outreachType,
        delayInMinutes: hours * MINUTES_PER_HOUR + minutes,
    });
    return {
        status: 'valid',
        delayInMinutes,
        displayValue: getOutreachStepDelayInputValue({
            index,
            outreachType,
            delayInMinutes,
        }),
        invalidParts,
        shouldReplaceInput: shouldReplaceHoursMinutesDraft({
            parsedHours,
            parsedMinutes,
        }),
    };
}
